import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { TimeRangeType, TimeRangeKey } from '../../components/time-picker/types';
import { TimeRange } from '../../models/time-range';
import {
  TIMEPICKER_OPTIONS,
  DEFAULT_TIMEPICKER_OPTION,
  TIMEPICKER_FUSION_OPTIONS
} from '../time-picker/timePickerOptions';
import { ValuesExchangeProvider } from './values.exchange.provider';
import { UserService } from '../user/user.service';

/**
 * This service provides a 'single source of truth' for time_range parameter.
 * Early versions of this framework were storing the time_range straight in values.exchange.provider,
 * so that service + subject was the 'single source of truth'.
 * Now, we want to be flexible on where the time_range is being stored (eg, it can be stored on the URL!).
 * Which means we can't treat the values.exchange.provider+subject as 'single source of truth' anymore.
 *
 * What's the relationship with values.exchange.provider? Well, it is recommended that widgets (who previously were
 * assumed to be the source of this value/thus the 'single source of truth') take care to subscribe to this
 * TimeRangeProvider as listeners - and whenever that value in TimeRangeProvider has changed, grab it and insert
 * into ValuesExchangeProvider.
 * This pattern guarantees that very little is changed from perspective of those, who were consuming the time_range
 * parameter. They will keep getting it from ValuesExchangeProvider.
 * Only the widget who was the 'master', now becomes a 'consumer' of this parameter - even if it continues
 * to work as mediator and inject a copy of this value into ValuesExchangeProvider.
 */
export abstract class TimeRangeProvider {
  protected timeRange$ = new BehaviorSubject<TimeRange>(null);

  constructor(
    private readonly valuesExchange: ValuesExchangeProvider,
    private readonly userService: UserService
  ) {
    this.setupValuesExhangeSync();
  }

  public notifyListeners(timeRange: TimeRange): void {
    this.timeRange$.next(timeRange);
  }

  public getCurrentValue(): TimeRange {
    return this.timeRange$.getValue();
  }

  public getActiveTimeRange$(): Observable<TimeRange> {
    return this.timeRange$.asObservable().pipe(filter((value: TimeRange) => value != null));
  }

  public makeFixedTimeRange(fromTime: number, toTime: number): TimeRange {
    const timeRange: TimeRange = this.getTemplate(TimeRangeKey.CUSTOM);
    timeRange.fromTime = fromTime;
    timeRange.toTime = toTime;
    return timeRange;
  }

  public transformToFixedTimeRange(timeRange: TimeRange): TimeRange {
    switch (timeRange?.type) {
      case TimeRangeType.MOVING_WINDOW: {
        const toTime = this.getStartDateNow();
        const fromTime = toTime - timeRange.timerange;
        return this.makeFixedTimeRange(fromTime, toTime);
      }
      case TimeRangeType.MONITORING_SESSION: {
        return this.makeFixedTimeRange(timeRange.fromTime, Date.now());
      }
      case TimeRangeType.FIXED_RANGE: {
        return this.makeFixedTimeRange(timeRange.fromTime, timeRange.toTime);
      }
      default:
        return this.makeFixedTimeRange(Date.now() - 3600000, Date.now());
    }
  }

  public getTemplate(key: TimeRangeKey): TimeRange {
    const timeOptions = this.userService.isFusionDashboardProject()
      ? TIMEPICKER_FUSION_OPTIONS
      : TIMEPICKER_OPTIONS;
    const option = timeOptions.find(item => item.timeRange.key === key);
    return option?.timeRange;
  }

  protected getDefaultTimeRange(): TimeRange {
    const defaultTimePickerOption = this.userService.isFusionDashboardProject()
      ? TIMEPICKER_FUSION_OPTIONS[1].timeRange
      : DEFAULT_TIMEPICKER_OPTION.timeRange;
    return cloneDeep(defaultTimePickerOption);
  }

  protected getStartDateNow(): number {
    const timeChunk = 1000 * 60 * 5;
    return Math.floor(Date.now() / timeChunk) * timeChunk;
  }

  private setupValuesExhangeSync(): void {
    this.timeRange$.subscribe((timeRange: TimeRange) => {
      if (timeRange) {
        this.valuesExchange.notifyListeners('time_range', cloneDeep(timeRange));
      }
    });
  }

  public abstract replaceTimeRange(selection: TimeRange): void;
}
